import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Brain, Send, Copy, Download, Users, Info, Package } from "lucide-react";
import { toast } from "sonner";
import TraitRadar from "@/components/TraitRadar";

const STRENGTH_STYLE = {
  Strong: "bg-teal text-white border-0",
  Moderate: "bg-amber-500 text-white border-0",
  Weak: "bg-red-500 text-white border-0",
};

const TRAIT_LABEL = {
  openness: "Openness",
  conscientiousness: "Conscientiousness",
  extraversion: "Extraversion",
  agreeableness: "Agreeableness",
  emotional_stability: "Emotional stability",
};

const TRAIT_DEFINITIONS = {
  openness: "Openness to experience — how curious, imaginative, and comfortable with novelty someone is. Higher = enjoys new ideas, exploration, unconventional approaches. Lower = prefers routine, tried-and-tested methods.",
  conscientiousness: "Conscientiousness — how organised, dependable, and self-disciplined someone is. Higher = plans carefully, follows through, respects deadlines. Lower = flexible, spontaneous, dislikes rigid processes.",
  extraversion: "Extraversion — where someone draws energy from. Higher = energised by people and group work, thinks out loud, speaks up early. Lower = recharges alone, prefers focused deep work.",
  agreeableness: "Agreeableness — how cooperative and empathetic someone is. Higher = seeks harmony, considers others' views, prefers compromise. Lower = direct, comfortable with disagreement and healthy debate.",
  emotional_stability: "Emotional stability — how calm someone stays under pressure. Higher = steady under stress, recovers quickly from setbacks. Lower = more emotionally reactive, may need more support during turbulent periods.",
};

const TraitLabel = ({ trait, className = "" }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className={`inline-flex items-center gap-1 cursor-help ${className}`} data-testid={`trait-tt-${trait}`}>
        {TRAIT_LABEL[trait] || trait}
        <Info className="w-3 h-3 text-neutral-400" />
      </span>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
      {TRAIT_DEFINITIONS[trait] || "—"}
    </TooltipContent>
  </Tooltip>
);

export default function BehavioralPanel({ candidateId, candidateName }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fit, setFit] = useState(null);
  const [fitLoading, setFitLoading] = useState(false);
  const [personalFit, setPersonalFit] = useState(null);

  const load = () => api.get(`/recruit/candidates/${candidateId}/behavioral`).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { if (candidateId) load(); }, [candidateId]);

  // Fetch team fit once behavioral is submitted
  useEffect(() => {
    if (!candidateId) return;
    const active = items[0];
    if (!active || active.status !== "SUBMITTED") { setFit(null); setPersonalFit(null); return; }
    setFitLoading(true);
    Promise.all([
      api.get(`/recruit/candidates/${candidateId}/team-fit`).catch(() => ({ data: null })),
      api.get(`/recruit/candidates/${candidateId}/personal-fit`).catch(() => ({ data: null })),
    ]).then(([tf, pf]) => { setFit(tf.data); setPersonalFit(pf.data); }).finally(() => setFitLoading(false));
  }, [candidateId, items]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/recruit/candidates/${candidateId}/behavioral-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(candidateName || "candidate").replace(/\s+/g, "_")}_brief.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Download failed"); }
  };

  const downloadOfferPacket = async () => {
    try {
      const res = await api.get(`/recruit/candidates/${candidateId}/offer-packet-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(candidateName || "candidate").replace(/\s+/g, "_")}_offer_packet.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Offer packet downloaded");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Download failed"); }
  };

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recruit/candidates/${candidateId}/behavioral-invite`);
      const url = `${window.location.origin}/behavioral/${data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success(data.email_mocked === false
        ? `Invite emailed to ${candidateName} · link also copied`
        : `Assessment link copied for ${candidateName}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const copyLink = async (a) => {
    const url = `${window.location.origin}/behavioral/${a.token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const active = items[0];

  return (
    <TooltipProvider delayDuration={200}>
    <Card className="border-0 shadow-sm" data-testid="behavioral-panel">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">Behavioural assessment</h3>
            {active?.status && <Badge className={active.status === "SUBMITTED" ? "bg-teal text-white border-0" : "bg-amber-100 text-amber-800 border-0"}>{active.status}</Badge>}
            {active?.ai_strength && <Badge className={STRENGTH_STYLE[active.ai_strength]}>{active.ai_strength}</Badge>}
          </div>
          {(!active || active.status !== "SUBMITTED") && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={invite} disabled={busy} data-testid="behavioral-invite-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> {active ? "Copy link" : "Invite behavioural"}
            </Button>
          )}
        </div>

        {!active && (
          <div className="text-neutral-500 text-sm py-4">
            Send {candidateName} a short 10-question behavioural check-in — Alfred will summarise the results for the hiring team.
          </div>
        )}

        {active?.status === "INVITED" && (
          <div className="text-sm text-neutral-600 flex items-center gap-2">
            <span className="italic">Invitation sent · waiting for completion.</span>
            <button onClick={() => copyLink(active)} className="text-pink text-xs hover:underline flex items-center gap-1" data-testid="behavioral-copy-link">
              <Copy className="w-3 h-3" /> Copy link
            </button>
          </div>
        )}

        {active?.status === "SUBMITTED" && active.scores && (
          <div className="space-y-4" data-testid="behavioral-result">
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="behavioral-pdf-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Brief PDF
              </Button>
              <Button size="sm" onClick={downloadOfferPacket} className="btn-primary rounded-full" data-testid="offer-packet-btn">
                <Package className="w-3.5 h-3.5 mr-1" /> Download offer packet
              </Button>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Big Five profile</div>
              <div className="space-y-2">
                {Object.entries(active.scores).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <div className="text-xs text-neutral-700 w-40 shrink-0"><TraitLabel trait={k} /></div>
                    <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                      <div className="h-full rounded-full bg-pink transition-all" style={{ width: `${v}%` }} />
                    </div>
                    <div className="font-mono font-bold text-sm w-10 text-right">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-neutral-500">Overall: <span className="font-mono font-bold text-neutral-900">{active.overall_score}</span> / 100</div>
            </div>
            {active.ai_summary && (
              <div className="border-t pt-4 text-sm space-y-2">
                <div className="text-xs uppercase tracking-widest text-neutral-500">Alfred's brief</div>
                <p className="text-neutral-800">{active.ai_summary.summary}</p>
                {active.ai_summary.strengths && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-600 mt-1 mb-1">Strengths</div>
                    <ul className="text-xs text-neutral-700 list-disc pl-5 space-y-0.5">{active.ai_summary.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
                {active.ai_summary.watch_outs && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-600 mt-2 mb-1">Watch-outs</div>
                    <ul className="text-xs text-neutral-700 list-disc pl-5 space-y-0.5">{active.ai_summary.watch_outs.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
                {active.ai_summary.fit_for_roles && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-600 mt-2 mb-1">Fit for roles</div>
                    <div className="flex flex-wrap gap-1.5">
                      {active.ai_summary.fit_for_roles.map((r, i) => <Badge key={i} variant="outline" className="text-xs">{r}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Team fit */}
            {fitLoading && <div className="text-xs text-neutral-500 italic">Computing team fit…</div>}
            {fit && fit.team_baseline && (
              <div className="border-t pt-4 text-sm space-y-3" data-testid="team-fit-block">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Users className="w-4 h-4 text-pink" />
                    <div className="text-xs uppercase tracking-widest text-neutral-500">Team fit</div>
                    {fit.verdict === "cultural_fit" && <Badge className="bg-teal text-white border-0" data-testid="fit-verdict">Cultural fit</Badge>}
                    {fit.verdict === "cultural_add" && <Badge className="bg-pink text-white border-0" data-testid="fit-verdict">Cultural add</Badge>}
                    {fit.verdict === "mixed" && <Badge className="bg-amber-500 text-white border-0" data-testid="fit-verdict">Mixed profile</Badge>}
                    {personalFit?.has_compass && personalFit?.has_behavioral && personalFit.verdict === "diverse" && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span data-testid="diverse-from-you-badge">
                            <Badge className="bg-navy text-white border-0 cursor-help" style={{ backgroundColor: "#00214D" }}>
                              Diverse from you
                            </Badge>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                          This candidate's Big Five differs from your hiring compass on {personalFit.differentiators.length} traits — a fresh perspective for your review.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="text-[10px] text-neutral-500">
                    Baseline: <span className="font-semibold">{fit.team_scope}</span> · {fit.sample_size} team member{fit.sample_size === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 items-center">
                  <TraitRadar candidate={fit.candidate} team={fit.team_baseline} size={240} />
                  <div className="space-y-2">
                    {Object.entries(fit.candidate).map(([k, cscore]) => {
                      const tscore = fit.team_baseline?.[k];
                      if (tscore == null) return null;
                      const delta = fit.deltas?.[k] || 0;
                      return (
                        <div key={k} className="text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-neutral-700"><TraitLabel trait={k} /></span>
                            <span className={`font-mono font-semibold ${Math.abs(delta) >= 15 ? "text-pink" : "text-neutral-400"}`}>
                              {delta > 0 ? "+" : ""}{delta}
                            </span>
                          </div>
                          <div className="relative h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                            <div className="absolute top-0 left-0 h-full bg-neutral-300" style={{ width: `${tscore}%` }} title={`Team avg: ${tscore}`} />
                            <div className="absolute top-0 h-full w-[3px] bg-pink" style={{ left: `calc(${cscore}% - 1.5px)` }} title={`Candidate: ${cscore}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {fit.differentiators?.length > 0 && (
                  <div className="text-xs text-neutral-600">
                    <span className="font-semibold text-neutral-700">Differentiator trait{fit.differentiators.length > 1 ? "s" : ""}: </span>
                    {fit.differentiators.map((d, i) => (
                      <span key={d.trait} className="inline-flex items-center gap-1">
                        {i > 0 && <span className="mx-1 text-neutral-400">·</span>}
                        <TraitLabel trait={d.trait} className="text-neutral-700" />
                        <span className={`font-mono ${d.direction === "higher" ? "text-teal" : "text-amber-600"}`}>({d.direction})</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-neutral-400 flex items-center gap-3">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-neutral-300" /> Team avg</span>
                  <span className="flex items-center gap-1"><span className="w-1 h-3 bg-pink" /> Candidate</span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
